import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";

import Home from "./pages/Home";
import ScrollToTopButton from "./Components/ScrollToTopButton";
import ScrollToTop from "./Components/ScrollToTop";
import WhatsAppWidget from "./Components/WhatsAppWidget";
import RouteFallback from "./Components/RouteFallback";
import ProtectedRoute from "./admin/components/ProtectedRoute";

// Public pages (lazy-loaded)
const About = lazy(() => import("./pages/about"));
const Contact = lazy(() => import("./pages/contact"));
const Deals = lazy(() => import("./pages/deals"));
const Login = lazy(() => import("./pages/login"));
const Signup = lazy(() => import("./pages/signup"));
const YachtDetails = lazy(() => import("./pages/yachtdetails"));
const Addons = lazy(() => import("./pages/addons"));
const Cart = lazy(() => import("./pages/cart"));
const ConfirmBooking = lazy(() => import("./pages/confirmbooking"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Refund = lazy(() => import("./pages/refund"));
const Terms = lazy(() => import("./pages/terms"));
const DataDeletion = lazy(() => import("./pages/DataDeletion"));
const ThankYou = lazy(() => import("./pages/thankyou"));
const Yachts = lazy(() => import("./pages/yachts"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Profile = lazy(() => import("./pages/profile"));
const OrderDetails = lazy(() => import("./pages/OrderDetails"));
const BirthdayLanding = lazy(() => import("./pages/birthdayLanding"));
const CorporateLanding = lazy(() => import("./pages/corporateLanding"));
const CorporateEventsLanding = lazy(
  () => import("./pages/corporateEventsLanding")
);
const FamilyPartyLanding = lazy(() => import("./pages/familyPartyLanding"));
const PreWeddingLanding = lazy(() => import("./pages/preWeddingLanding"));
const AlibaugLanding = lazy(() => import("./pages/alibaugLanding"));
const PrivateYacht = lazy(() => import("./pages/PrivateYacht"));
const PartyYachtMumbai = lazy(() => import("./pages/partyyachtmumbai"));
const Speedboattransfer = lazy(() => import("./pages/speedboattransfer"));
const CorporateSailingEventsMumbai = lazy(
  () => import("./pages/corporateSailingEventsMumbai")
);
const Alibaug = lazy(() => import("./pages/Alibaug"));
const CorporateEvents = lazy(() => import("./pages/CorporateEvents"));
const FamilyEvents = lazy(() => import("./pages/FamilyEvents"));
const PreWedding = lazy(() => import("./pages/PreWedding"));
const SpeedBoat = lazy(() => import("./pages/SpeedBoat"));
const YachtParties = lazy(() => import("./pages/yachtParties"));
const Regattas = lazy(() => import("./pages/Regattas"));
const CorporateParties = lazy(() => import("./pages/CorporateParties"));
const CorporateNetworking = lazy(() => import("./pages/CorporateNetworking"));
const Birthdays = lazy(() => import("./pages/Birthdays"));
const SunsetCruiseMumbai = lazy(() => import("./pages/SunsetCruiseMumbai"));
const SpeedBoatMumbaiToElephanta = lazy(
  () => import("./pages/SpeedBoatMumbaiToElephanta")
);
const SpeedBoatMumbaiToMandwa = lazy(
  () => import("./pages/SpeedBoatMumbaiToMandwa")
);
const YachtBookingGatewayOfIndia = lazy(
  () => import("./pages/YachtBookingGatewayOfIndia")
);
const CouplesYachtMumbai = lazy(() => import("./pages/CouplesYachtMumbai"));
const YachtProposalMumbai = lazy(() => import("./pages/YachtProposalMumbai"));
const BachelorPartyYachtMumbai = lazy(
  () => import("./pages/BachelorPartyYachtMumbai")
);
const LearnSailingMumbai = lazy(() => import("./pages/LearnSailingMumbai"));
const SailWithAChampionMumbai = lazy(
  () => import("./pages/SailWithAChampionMumbai")
);
const YachtRentalGoa = lazy(() => import("./pages/YachtRentalGoa"));
const YachtPartyGoa = lazy(() => import("./pages/YachtPartyGoa"));
const SunsetCruiseGoa = lazy(() => import("./pages/SunsetCruiseGoa"));
const FleetNemo = lazy(() => import("./pages/FleetNemo"));
const FleetAriel = lazy(() => import("./pages/FleetAriel"));
const FleetAaniaa = lazy(() => import("./pages/FleetAaniaa"));
const GuideYachtPartyCostMumbai = lazy(
  () => import("./pages/guides/GuideYachtPartyCostMumbai")
);
const GuideAlibaugSpeedBoatVsFerry = lazy(
  () => import("./pages/guides/GuideAlibaugSpeedBoatVsFerry")
);
const GuideElephantaBySpeedBoat = lazy(
  () => import("./pages/guides/GuideElephantaBySpeedBoat")
);
const GuideYachtProposalChecklist = lazy(
  () => import("./pages/guides/GuideYachtProposalChecklist")
);
const GuideFirstYachtRideTips = lazy(
  () => import("./pages/guides/GuideFirstYachtRideTips")
);
const GuideSailingYachtVsMotorYacht = lazy(
  () => import("./pages/guides/GuideSailingYachtVsMotorYacht")
);
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Family = lazy(() => import("./pages/Family"));
const Packages = lazy(() => import("./pages/Packages"));
const Gift = lazy(() => import("./pages/Gift"));
const Gallery = lazy(() => import("./pages/Gallery"));
const AlibaugElephantaBoatTours = lazy(
  () => import("./pages/AlibaugElephantaBoatTours")
);

// Admin (lazy-loaded — not downloaded on public pages)
const AdminLogin = lazy(() => import("./admin/pages/AdminLogin"));
const AdminLayout = lazy(() => import("./admin/components/AdminLayout"));
const AdminDashboard = lazy(() => import("./admin/pages/AdminDashboard"));
const SignatureExperience = lazy(
  () => import("./admin/pages/SignatureExperience")
);
const MostLovedExperiences = lazy(
  () => import("./admin/pages/MostLovedExperiences")
);
const TestimonialsVideos = lazy(
  () => import("./admin/pages/TestimonialsVideos")
);
const PackagesCms = lazy(() => import("./admin/pages/Packages"));
const BrowsePackages = lazy(() => import("./admin/pages/BrowsePackages"));
const Faqs = lazy(() => import("./admin/pages/Faqs"));
const Experiences = lazy(() => import("./admin/pages/Experiences"));
const PrivateYachtCms = lazy(() => import("./admin/pages/PrivateYachtCms"));
const YachtPartiesCms = lazy(() => import("./admin/pages/YachtPartiesCms"));
const SpeedBoatCms = lazy(() => import("./admin/pages/SpeedBoatCms"));
const SpeedBoatRates = lazy(() => import("./admin/pages/SpeedBoatRates"));
const AddonsCms = lazy(() => import("./admin/pages/AddonsCms"));
const Enquiries = lazy(() => import("./admin/pages/Enquiries"));
const Newsletter = lazy(() => import("./admin/pages/Newsletter"));
const TimeSlots = lazy(() => import("./admin/pages/TimeSlots"));
const YachtDetailsCms = lazy(() => import("./admin/pages/YachtDetails"));
const Signups = lazy(() => import("./admin/pages/Signups"));
const ConfirmedBookings = lazy(
  () => import("./admin/pages/ConfirmedBookings")
);
const BlogPostsCms = lazy(() => import("./admin/pages/BlogPosts"));

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Main Pages */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/deals" element={<Deals />} />
            <Route path="/yachts" element={<Yachts />} />

            {/* Auth */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />

            {/* Booking Flow */}
            <Route path="/cart" element={<Cart />} />
            <Route path="/addons" element={<Addons />} />
            <Route path="/confirmbooking" element={<ConfirmBooking />} />
            <Route path="/thankyou/:id" element={<ThankYou />} />

            {/* Legal Pages */}
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/refund" element={<Refund />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/data-deletion" element={<DataDeletion />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/orderdetails" element={<OrderDetails />} />
            <Route
              path="/OrderDetails"
              element={<Navigate to="/orderdetails" replace />}
            />
            <Route path="/birthdaylanding" element={<BirthdayLanding />} />
            <Route
              path="/BirthdayLanding"
              element={<Navigate to="/birthdaylanding" replace />}
            />
            <Route path="/corporatelanding" element={<CorporateLanding />} />
            <Route
              path="/CorporateLanding"
              element={<Navigate to="/corporatelanding" replace />}
            />
            <Route
              path="/corporateeventslanding"
              element={<CorporateEventsLanding />}
            />
            <Route
              path="/CorporateEventsLanding"
              element={<Navigate to="/corporateeventslanding" replace />}
            />
            <Route path="/familypartylanding" element={<FamilyPartyLanding />} />
            <Route
              path="/FamilyPartyLanding"
              element={<Navigate to="/familypartylanding" replace />}
            />
            <Route path="/preweddinglanding" element={<PreWeddingLanding />} />
            <Route
              path="/PreWeddingLanding"
              element={<Navigate to="/preweddinglanding" replace />}
            />
            <Route path="/alibauglanding" element={<AlibaugLanding />} />
            <Route
              path="/AlibaugLanding"
              element={<Navigate to="/alibauglanding" replace />}
            />
            <Route
              path="/private-yacht-charter-mumbai-goa"
              element={<PrivateYacht />}
            />
            <Route path="/party-yacht-mumbai-goa" element={<PartyYachtMumbai />} />
            <Route
              path="/speedboat-transfer-mumbai"
              element={<Speedboattransfer />}
            />
            <Route
              path="/corporate-sailing-events-mumbai"
              element={<CorporateSailingEventsMumbai />}
            />
            <Route path="/alibaug" element={<Alibaug />} />
            <Route path="/Alibaug" element={<Navigate to="/alibaug" replace />} />
            <Route path="/corporate-events" element={<CorporateEvents />} />
            <Route
              path="/CorporateEvents"
              element={<Navigate to="/corporate-events" replace />}
            />
            <Route path="/familyevents" element={<FamilyEvents />} />
            <Route
              path="/FamilyEvents"
              element={<Navigate to="/familyevents" replace />}
            />
            <Route
              path="/pre-wedding-yacht-photoshoot-mumbai"
              element={<PreWedding />}
            />
            <Route path="/speedboat" element={<SpeedBoat />} />
            <Route path="/SpeedBoat" element={<Navigate to="/speedboat" replace />} />
            <Route path="/yacht-party-mumbai" element={<YachtParties />} />
            <Route
              path="/corporate-sailing-regatta-mumbai"
              element={<Regattas />}
            />
            <Route
              path="/corporate-party-awards-yacht-mumbai"
              element={<CorporateParties />}
            />
            <Route
              path="/corporate-events-networking-yacht-mumbai"
              element={<CorporateNetworking />}
            />
            <Route
              path="/corporate-networking-events"
              element={<Navigate to="/corporate-events" replace />}
            />
            <Route
              path="/corporate-sailing-regattas"
              element={<Navigate to="/corporate-events" replace />}
            />
            <Route
              path="/corporate-yacht-parties"
              element={<Navigate to="/corporate-events" replace />}
            />
            <Route
              path="/birthday-anniversary-yacht-celebration-mumbai"
              element={<Birthdays />}
            />
            <Route
              path="/sunset-cruise-mumbai"
              element={<SunsetCruiseMumbai />}
            />
            <Route
              path="/speed-boat-mumbai-to-elephanta"
              element={<SpeedBoatMumbaiToElephanta />}
            />
            <Route
              path="/speed-boat-mumbai-to-mandwa"
              element={<SpeedBoatMumbaiToMandwa />}
            />
            <Route
              path="/yacht-booking-gateway-of-india"
              element={<YachtBookingGatewayOfIndia />}
            />
            <Route
              path="/couples-yacht-mumbai"
              element={<CouplesYachtMumbai />}
            />
            <Route
              path="/yacht-proposal-mumbai"
              element={<YachtProposalMumbai />}
            />
            <Route
              path="/bachelor-party-yacht-mumbai"
              element={<BachelorPartyYachtMumbai />}
            />
            <Route
              path="/learn-sailing-mumbai"
              element={<LearnSailingMumbai />}
            />
            <Route
              path="/sail-with-a-champion-mumbai"
              element={<SailWithAChampionMumbai />}
            />
            <Route path="/yacht-rental-goa" element={<YachtRentalGoa />} />
            <Route path="/yacht-party-goa" element={<YachtPartyGoa />} />
            <Route path="/sunset-cruise-goa" element={<SunsetCruiseGoa />} />
            <Route path="/fleet/nemo" element={<FleetNemo />} />
            <Route path="/fleet/ariel" element={<FleetAriel />} />
            <Route path="/fleet/aaniaa" element={<FleetAaniaa />} />
            <Route
              path="/guides/yacht-party-cost-mumbai"
              element={<GuideYachtPartyCostMumbai />}
            />
            <Route
              path="/guides/alibaug-speed-boat-vs-ferry"
              element={<GuideAlibaugSpeedBoatVsFerry />}
            />
            <Route
              path="/guides/elephanta-by-speed-boat"
              element={<GuideElephantaBySpeedBoat />}
            />
            <Route
              path="/guides/yacht-proposal-checklist"
              element={<GuideYachtProposalChecklist />}
            />
            <Route
              path="/guides/first-yacht-ride-tips"
              element={<GuideFirstYachtRideTips />}
            />
            <Route
              path="/guides/sailing-yacht-vs-motor-yacht"
              element={<GuideSailingYachtVsMotorYacht />}
            />
            <Route path="/family-private-yacht-party-mumbai" element={<Family />} />
            <Route
              path="/alibaug-elephanta-boat-tours-mumbai"
              element={<AlibaugElephantaBoatTours />}
            />
            <Route path="/packages" element={<Packages />} />
            <Route path="/gift" element={<Gift />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            <Route path="/yachtdetails/:slug" element={<YachtDetails />} />

            <Route path="/admin/login" element={<AdminLogin />} />

            <Route element={<ProtectedRoute />}>
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<AdminDashboard />} />
                <Route
                  path="signature-experience"
                  element={<SignatureExperience />}
                />
                <Route
                  path="most-loved-experiences"
                  element={<MostLovedExperiences />}
                />
                <Route path="testimonials" element={<TestimonialsVideos />} />
                <Route path="packages" element={<PackagesCms />} />
                <Route path="browse-packages" element={<BrowsePackages />} />
                <Route path="faqs" element={<Faqs />} />
                <Route path="experiences" element={<Experiences />} />
                <Route path="private-yacht" element={<PrivateYachtCms />} />
                <Route path="yacht-parties" element={<YachtPartiesCms />} />
                <Route path="speed-boats" element={<SpeedBoatCms />} />
                <Route path="enquiries" element={<Enquiries />} />
                <Route path="newsletter" element={<Newsletter />} />
                <Route path="time-slots" element={<TimeSlots />} />
                <Route path="yacht-details" element={<YachtDetailsCms />} />
                <Route path="speedboat-times" element={<SpeedBoatRates />} />
                <Route path="addons" element={<AddonsCms />} />
                <Route path="signups" element={<Signups />} />
                <Route
                  path="confirmed-bookings"
                  element={<ConfirmedBookings />}
                />
                <Route path="blog-posts" element={<BlogPostsCms />} />
              </Route>
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>

        <ScrollToTopButton />
        <WhatsAppWidget />
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
